import { GeoJson, GeoJsonTypeEnum } from '@ais-ui/api-spec/shapes';
import { isNonEmpty, isPresent } from '@ais-ui/utils';

import { shapeToIcon } from './shapeToIcon';
import React from 'react';
import { IconType } from 'react-icons';
import { FlattenedShapeItem } from './types';

const filterShapeByName = (shape: GeoJson, name: string): GeoJson | null => {
  // If the name is empty, return the shape as is
  if (!name) {
    return shape;
  }

  // Check if the current shape matches the name
  if (shape.properties?.name?.toLowerCase().includes(name)) {
    return shape;
  }

  // If it's a FeatureCollection, recursively search its features
  if (shape.type === 'FeatureCollection' && shape.features) {
    const filteredFeatures = shape.features
      .map((feature) => filterShapeByName(feature, name))
      .filter(isPresent); // Keep only non-null features

    // If any features match, return a new FeatureCollection with those features
    if (filteredFeatures.length > 0) {
      return { ...shape, features: filteredFeatures };
    }
  }

  // If no match was found in this shape or its children, return null
  return null;
};

interface Geometry {
  type: string;
  coordinates: any;
}

const filterShapeByGeometryTypes = (
  shape: GeoJson,
  allowedGeometryTypes: string[],
  allowEmptyGroups = false
): GeoJson | null => {
  const lowerAllowedTypes = allowedGeometryTypes.map((type) =>
    type.toLowerCase()
  );

  const isTwoPointLineString = (geometry: Geometry): boolean => {
    return (
      geometry.type.toLowerCase() === 'linestring' &&
      geometry.coordinates.length === 2
    );
  };

  const isCircle = (shape: GeoJson): boolean => {
    return isNonEmpty(shape?.properties?.radius);
  };

  // Check for FeatureCollection and process recursively
  if (shape.type === 'FeatureCollection' && shape.features) {
    const filteredFeatures = shape.features
      .map((feature) =>
        filterShapeByGeometryTypes(
          feature,
          allowedGeometryTypes,
          allowEmptyGroups
        )
      )
      .filter(isPresent);

    if (allowEmptyGroups || filteredFeatures.length > 0) {
      return { ...shape, features: filteredFeatures };
    } else {
      return null;
    }
  } else if (shape.type === 'Feature' && shape.geometry) {
    // Check if the feature's geometry type is allowed or if it's a TwoPointLineString or Circle
    //  TwoPointLineString and Circle are special cases
    let geometryType = shape.geometry.type.toLowerCase();

    // We need to make sure we are checking explicitly for twopointlinestring else we will not
    //  see two point linestrings if only linestrings are chosen
    if (
      geometryType === 'linestring' &&
      isTwoPointLineString(shape.geometry) &&
      lowerAllowedTypes.includes('twopointlinestring')
    ) {
      geometryType = 'twopointlinestring';
    } else if (geometryType === 'point' && isCircle(shape)) {
      geometryType = 'circle';
    }

    if (lowerAllowedTypes.includes(geometryType)) {
      return shape;
    }
  }

  // If the shape does not match the criteria, return null
  return null;
};

export const filterShapes = (
  shapes: GeoJson[],
  name: string,
  allowedGeometryTypes: string[],
  allowEmptyGroups = false
) => {
  const lowercaseName = name.toLowerCase();

  const availableShapes = shapes
    .map((shape) => filterShapeByName(shape, lowercaseName))
    .filter(isPresent);

  if (allowedGeometryTypes.length > 0) {
    return availableShapes
      .map((shape) =>
        filterShapeByGeometryTypes(
          shape,
          allowedGeometryTypes,
          allowEmptyGroups
        )
      )
      .filter(isPresent);
  }

  return availableShapes;
};

const filterShapeGroupByName = (
  shape: GeoJson,
  name: string
): GeoJson | null => {
  if (shape.properties.name?.toLowerCase().includes(name)) {
    return shape;
  }

  if (
    shape.type === 'Feature' ||
    shape.features == null ||
    shape.features.length === 0
  ) {
    return null;
  }

  const filteredFeatures = filterShapeGroups(shape.features, name);
  if (filteredFeatures.length > 0) {
    return { ...shape, features: filteredFeatures };
  }

  return null;
};

export const filterShapeGroups = (
  shapes: GeoJson[],
  name: string
): GeoJson[] => {
  const lowercaseName = name.toLowerCase();

  const availableShapes: GeoJson[] = shapes
    .map((shape) => filterShapeGroupByName(shape, lowercaseName))
    .filter(isPresent);
  return availableShapes;
};

export function setParentIdsOnShapes(shapes: GeoJson[] | undefined): void {
  if (!shapes) {
    return;
  }

  shapes.forEach((shape) => {
    setParentId(shape);
  });
}

export function setParentId(featureCollection: GeoJson): void {
  if (!featureCollection || !featureCollection.features) return;

  const parentId = featureCollection.properties.id;

  for (const feature of featureCollection.features) {
    if (
      feature.type === GeoJsonTypeEnum.FeatureCollection &&
      feature.features
    ) {
      feature.properties.parentId = parentId;
      // Recursively process nested feature collections
      setParentId(feature);
    } else if (feature.type === GeoJsonTypeEnum.Feature && parentId) {
      feature.properties.parentId = parentId;
    }
  }
}

export interface ShapeSelectType {
  id: string | undefined;
  name: string | undefined;
  type: string;
  icon: IconType | React.FC<React.SVGProps<SVGSVGElement>>;
  level: number;
  geometryType: string;
}

export function flattenShapes(shape: GeoJson, level = 0): ShapeSelectType[] {
  const ShapeIcon = shapeToIcon(shape);

  let geometryType = 'unknown';

  if (shape.geometry && shape.geometry.type) {
    if (shape.geometry.type === 'Point' && shape.properties?.radius) {
      geometryType = 'Circle';
    } else {
      geometryType = shape.geometry.type;
    }
  }

  // Include the feature collection itself in the output
  const shapeDetails: ShapeSelectType = {
    id: shape.properties?.id,
    name: shape.properties?.name,
    type: shape.type,
    icon: ShapeIcon,
    level,
    geometryType: geometryType,
  };

  // If the shape is not a feature collection, return only its details
  if (shape.type !== 'FeatureCollection' || !shape.features) {
    return [shapeDetails];
  }

  // If it's a feature collection, include its details and flatten nested features
  const nestedFeatures = shape.features.flatMap((nestedShape) =>
    flattenShapes(nestedShape, level + 1)
  );

  return [shapeDetails, ...nestedFeatures];
}

/**
 * Purpose-built function for flattening shapes for accordion virtualization
 * More efficient than converting from existing flattenShapes
 */
export function flattenShapesForAccordion(
  shapes: GeoJson[],
  openAccordions: Set<string>
): FlattenedShapeItem[] {
  const result: FlattenedShapeItem[] = [];

  const processShape = (
    shape: GeoJson,
    level: number = 0,
    parentId?: string,
    ancestorIds: string[] = [] // Track all ancestor IDs for visibility calculation
  ): void => {
    const shapeId = shape.properties?.id;
    const hasChildren =
      shape.type === 'FeatureCollection' &&
      Array.isArray(shape.features) &&
      shape.features.length > 0;

    // Calculate visibility: item is visible if ALL its ancestors have open accordions
    const isVisible =
      level === 0 ||
      ancestorIds.every((ancestorId) => openAccordions.has(ancestorId));

    const flattenedItem: FlattenedShapeItem = {
      shape,
      level,
      parentId,
      isVisible,
      hasChildren,
      flattenedId: `${level}-${shapeId || `unknown-${result.length}`}`,
    };

    result.push(flattenedItem);

    // Process children if this is a FeatureCollection
    if (hasChildren && shape.features) {
      // Add current shape ID to ancestor chain for children
      const newAncestorIds = shapeId ? [...ancestorIds, shapeId] : ancestorIds;

      for (const childShape of shape.features) {
        processShape(childShape, level + 1, shapeId, newAncestorIds);
      }
    }
  };

  // Process all root shapes
  for (const shape of shapes) {
    processShape(shape);
  }

  return result;
}

/**
 * Filters flattened items to only return visible ones
 */
export function getVisibleFlattenedItems(
  flattenedItems: FlattenedShapeItem[]
): FlattenedShapeItem[] {
  return flattenedItems.filter((item) => item.isVisible);
}

/**
 * Gets a slice of visible items for pagination/infinite scroll
 */
export function getVisibleItemsSlice(
  flattenedItems: FlattenedShapeItem[],
  offset = 0,
  limit = 50
): FlattenedShapeItem[] {
  const visibleItems = getVisibleFlattenedItems(flattenedItems);
  return visibleItems.slice(offset, offset + limit);
}
